import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { AiReply, AiService, AiSource, AiStreamEvent } from 'src/app/services/ai.service';
import { Domain } from 'src/app/services/domain.service';
import { selectDomain } from 'src/app/store/selectors/auth.selectors';

interface AiMessage {
  role: 'user' | 'assistant';
  content?: string;
  reply?: AiReply;
  sources?: AiSource[];
  sourceGroups?: AiSourceGroup[];
  error?: string;
}

interface AiSourceItem extends AiSource {
  key: string;
  passage: string;
}

interface AiSourceGroup {
  key: string;
  title: string;
  source_url?: string;
  items: AiSourceItem[];
}

const CONTACT_CSE_LINK_PATTERN = /\[\[CONTACT_CSE_LINK\]\]([^\r\n]*?)\[\[\/CONTACT_CSE_LINK\]\]/gi;
const CONTACT_CSE_MARKER_PATTERN = /\[\[\/?CONTACT_CSE_LINK\]\]/gi;

@Component({
  selector: 'mvp-ai-assistant-overlay',
  templateUrl: './ai-assistant-overlay.component.html',
  styleUrls: ['./ai-assistant-overlay.component.scss'],
})
export class AiAssistantOverlayComponent implements OnInit, OnChanges, OnDestroy, AfterViewChecked {
  @Input() firstMessage?: string;
  @Output() close = new EventEmitter<void>();
  @ViewChild('thread') thread?: ElementRef<HTMLDivElement>;

  domain?: Domain;
  question = '';
  messages: AiMessage[] = [];
  conversationId?: number;
  loading = false;
  receivingText = false;
  openSources: Record<string, boolean> = {};
  private shouldScroll = false;
  private stickToBottom = true;
  private lastThreadScrollTop = 0;
  private consumedFirstMessage?: string;
  private streamSubscription?: Subscription;
  private textRevealTimer?: ReturnType<typeof setInterval>;

  constructor(
    private aiService: AiService,
    private cdr: ChangeDetectorRef,
    private store: Store,
    private router: Router,
  ) {}

  ngOnInit() {
    if (typeof document !== 'undefined') {
      (document.activeElement as HTMLElement | null)?.blur?.();
    }

    this.store.select(selectDomain).pipe(take(1)).subscribe((domain: Domain) => {
      this.domain = domain;
      this.sendFirstMessage();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if ('firstMessage' in changes) {
      this.sendFirstMessage();
    }
  }

  ngAfterViewChecked() {
    if (!this.shouldScroll) {
      return;
    }

    this.shouldScroll = false;
    const el = this.thread?.nativeElement;
    if (el) {
      if (!this.stickToBottom) {
        return;
      }
      el.scrollTop = el.scrollHeight;
      this.lastThreadScrollTop = el.scrollTop;
    }
  }

  ngOnDestroy() {
    this.streamSubscription?.unsubscribe();
    this.clearTextRevealTimer();
  }

  resetConversation() {
    this.streamSubscription?.unsubscribe();
    this.clearTextRevealTimer();
    this.loading = false;
    this.receivingText = false;
    this.messages = [];
    this.conversationId = undefined;
    this.openSources = {};
    this.stickToBottom = true;
    this.requestScroll(true);
  }

  send(explicitQuestion?: string) {
    const question = (explicitQuestion || this.question || '').trim();
    if (!question || this.loading) {
      return;
    }

    this.messages.push({ role: 'user', content: question });
    this.question = '';
    this.loading = true;
    this.receivingText = false;
    this.stickToBottom = true;
    this.requestScroll(true);

    let streamedMessage: AiMessage | undefined;
    let pendingTextSegments: Array<{ text: string }> = [];
    let structuredData: AiStreamEvent['data'] | undefined;
    let streamFinished = false;
    let streamFailed = false;

    const ensureStreamedMessage = (): AiMessage => {
      if (!streamedMessage) {
        streamedMessage = { role: 'assistant', reply: { main: '' }, sources: [] };
        this.messages.push(streamedMessage);
      }

      return streamedMessage;
    };

    const applyStructuredData = () => {
      if (!structuredData?.reply) {
        return;
      }

      const message = ensureStreamedMessage();
      const sources = this.mergeCitedAndRetrievedSources(
        structuredData.reply.sources,
        structuredData.rag_sources,
      );
      message.reply = structuredData.reply;
      message.sources = sources;
      message.sourceGroups = this.buildSourceGroups(sources, this.messages.indexOf(message));
      structuredData = undefined;
    };

    const finishIfReady = () => {
      if (!streamFinished || pendingTextSegments.length || this.textRevealTimer) {
        return;
      }

      if (!streamFailed) {
        applyStructuredData();
      }
      this.loading = false;
      this.receivingText = false;
      this.requestScroll();
      this.cdr.detectChanges();
    };

    const startTextReveal = () => {
      if (this.textRevealTimer) {
        return;
      }

      this.textRevealTimer = setInterval(() => {
        if (!pendingTextSegments.length) {
          this.clearTextRevealTimer();
          applyStructuredData();
          finishIfReady();
          return;
        }

        const segment = pendingTextSegments[0];
        const charactersPerFrame = Math.min(14, Math.max(2, Math.ceil(segment.text.length / 24)));
        const visibleText = segment.text.slice(0, charactersPerFrame);
        segment.text = segment.text.slice(charactersPerFrame);
        if (!segment.text) {
          pendingTextSegments.shift();
        }
        const message = ensureStreamedMessage();
        const reply = message.reply || { main: '' };
        message.reply = reply;
        reply.main = `${reply.main || ''}${visibleText}`;
        this.receivingText = true;
        this.requestScroll();
        this.cdr.detectChanges();
      }, 45);
    };

    this.streamSubscription = this.aiService.stream(question, this.domain?.uuid, this.conversationId).subscribe({
      next: (event: AiStreamEvent) => {
        if (event.event === 'meta') {
          this.conversationId = event.data.conversation_id || this.conversationId;
          return;
        }

        if (event.event === 'text' && event.data.text) {
          pendingTextSegments.push({
            text: event.data.text,
          });
          startTextReveal();
          return;
        }

        if (event.event === 'structured' && event.data.reply) {
          structuredData = event.data;
          if (!pendingTextSegments.length && !this.textRevealTimer) {
            applyStructuredData();
            this.requestScroll();
            this.cdr.detectChanges();
          }
          return;
        }

        if (event.event === 'error') {
          pendingTextSegments = [];
          structuredData = undefined;
          streamFailed = true;
          this.clearTextRevealTimer();
          const error = event.data.message || 'Une erreur est survenue, veuillez réessayer.';
          if (streamedMessage) {
            streamedMessage.error = error;
            streamedMessage.reply = undefined;
          } else {
            this.messages.push({ role: 'assistant', error });
          }
          this.cdr.detectChanges();
        }
      },
      error: (err) => {
        pendingTextSegments = [];
        structuredData = undefined;
        streamFailed = true;
        streamFinished = true;
        this.clearTextRevealTimer();
        const error = this.errorMessage(err);
        if (streamedMessage) {
          streamedMessage.error = error;
          streamedMessage.reply = undefined;
        } else {
          this.messages.push({ role: 'assistant', error });
        }
        finishIfReady();
      },
      complete: () => {
        streamFinished = true;
        finishIfReady();
      },
    });
  }

  onComposerKeydown(event: KeyboardEvent) {
    if (event.key !== 'Enter' || event.shiftKey) {
      return;
    }

    event.preventDefault();
    this.send();
  }

  renderMarkdown(value?: string, linkContactCse = false): string {
    if (!value) {
      return '';
    }

    const escaped = this.escapeHtml(value);
    const content = this.renderContactCseLinkMarkers(escaped, linkContactCse);
    const lines = content.split(/\r?\n/);
    const blocks: string[] = [];
    let listItems: string[] = [];

    const flushList = () => {
      if (!listItems.length) {
        return;
      }
      blocks.push(`<ul>${listItems.map(item => `<li>${this.inlineMarkdown(item)}</li>`).join('')}</ul>`);
      listItems = [];
    };

    for (const rawLine of lines) {
      const line = rawLine.trim();
      if (!line) {
        flushList();
        continue;
      }

      const bullet = line.match(/^[-*]\s+(.+)$/);
      if (bullet) {
        listItems.push(bullet[1]);
        continue;
      }

      flushList();
      blocks.push(`<p>${this.inlineMarkdown(line)}</p>`);
    }

    flushList();

    return blocks.join('');
  }

  groupedSources(message: AiMessage): AiSourceGroup[] {
    if (Array.isArray(message.sourceGroups)) {
      return message.sourceGroups;
    }

    return this.buildSourceGroups(message.sources, this.messages.indexOf(message));
  }

  followUps(reply?: AiReply): string[] {
    const list = reply?.follow_up_questions;
    return Array.isArray(list)
      ? list.filter(item => typeof item === 'string' && item.trim()).slice(0, 3)
      : [];
  }

  canContactCse(): boolean {
    return this.domain?.chat?.is_activated === true
      && this.domain?.contact?.is_activated === true
      && typeof this.domain?.contact?.email === 'string'
      && this.domain.contact.email.trim().length > 0;
  }

  contactCse() {
    this.close.emit();
    this.router.navigate(['/new-chat'], {
      queryParams: { recipient: 'cse' },
    });
  }

  onAnswerClick(event: Event) {
    const target = event.target;
    if (!(target instanceof Element) || !target.closest('[data-contact-cse-link]')) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.contactCse();
  }

  openSourceUrl(event: Event, url?: string) {
    event.preventDefault();
    event.stopPropagation();

    if (!url) {
      return;
    }

    this.openInNewTab(url);
  }

  toggleSource(event: Event, key: string) {
    event.preventDefault();
    event.stopPropagation();

    this.openSources = {
      ...this.openSources,
      [key]: !this.openSources[key],
    };
    this.cdr.detectChanges();
  }

  openCitation(event: Event, item: AiSourceItem) {
    event.preventDefault();
    event.stopPropagation();

    if (item.viewer_url) {
      this.openSourceUrl(event, item.viewer_url);
      return;
    }

    this.toggleSource(event, item.key);
  }

  onThreadScroll() {
    const el = this.thread?.nativeElement;
    if (!el) {
      return;
    }

    const isScrollingUp = el.scrollTop < this.lastThreadScrollTop - 2;
    if (this.loading && isScrollingUp) {
      this.stickToBottom = false;
    } else if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
      this.stickToBottom = true;
    } else if (this.loading) {
      this.stickToBottom = false;
    }

    if (!this.stickToBottom) {
      this.shouldScroll = false;
    }

    this.lastThreadScrollTop = el.scrollTop;
  }

  private clearTextRevealTimer() {
    if (this.textRevealTimer) {
      clearInterval(this.textRevealTimer);
      this.textRevealTimer = undefined;
    }
  }

  private sendFirstMessage() {
    const firstMessage = (this.firstMessage || '').trim();
    if (!firstMessage || this.consumedFirstMessage === firstMessage || !this.domain) {
      return;
    }

    this.consumedFirstMessage = firstMessage;
    this.send(firstMessage);
  }

  private errorMessage(err: any): string {
    const response = err?.error || {};
    if (err?.status === 429) {
      return response.message || 'Quota mensuel atteint pour ce domaine.';
    }
    if (err?.status === 403) {
      return response.message || 'Accès refusé : rôle insuffisant pour interroger l’assistant.';
    }
    return 'Une erreur est survenue, veuillez réessayer.';
  }

  private inlineMarkdown(value: string): string {
    return value
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\[(.+?)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');
  }

  private renderContactCseLinkMarkers(value: string, linkContactCse: boolean): string {
    const rendered = value.replace(
      CONTACT_CSE_LINK_PATTERN,
      linkContactCse
        ? '<a href="/new-chat?recipient=cse" data-contact-cse-link="true">$1</a>'
        : '$1',
    );

    return rendered.replace(CONTACT_CSE_MARKER_PATTERN, '');
  }

  private buildSourceGroups(sourcesInput: AiSource[] | undefined, messageIndex = 0): AiSourceGroup[] {
    const sources = Array.isArray(sourcesInput) ? sourcesInput : [];
    const groups: AiSourceGroup[] = [];

    sources.forEach((source: AiSource) => {
      const title = source.title || source.doc_title || 'Document';
      const key = source.doc_uuid || title;
      let group = groups.find(item => item.key === key);

      if (!group) {
        group = {
          key,
          title,
          source_url: source.viewer_url || source.source_url,
          items: [],
        };
        groups.push(group);
      } else if (!group.source_url && (source.viewer_url || source.source_url)) {
        group.source_url = source.viewer_url || source.source_url;
      }

      const passage = this.sourcePassage(source);
      const duplicate = group.items.some(item =>
        (item.article_ref || '') === (source.article_ref || '') &&
        item.passage === passage
      );

      if (!duplicate) {
        group.items.push({
          ...source,
          key: `${messageIndex}-${groups.length - 1}-${group.items.length}`,
          passage,
        });
      }
    });

    return groups;
  }

  private mergeCitedAndRetrievedSources(citedInput?: AiSource[], retrievedInput?: AiSource[]): AiSource[] {
    const cited = Array.isArray(citedInput) ? citedInput : [];
    const retrieved = Array.isArray(retrievedInput) ? retrievedInput : [];

    if (!cited.length) {
      return retrieved;
    }

    return cited.map((source: AiSource) => {
      const match = this.findRetrievedSourceForCitation(source, retrieved);
      const title = source.title || source.doc_title || match?.title || match?.doc_title || 'Document';

      return {
        ...(match || {}),
        ...source,
        title,
        doc_title: source.doc_title || source.title || match?.doc_title || match?.title || 'Document',
        source_url: source.source_url || match?.source_url,
        viewer_url: source.viewer_url || match?.viewer_url,
        content: source.content || match?.content || '',
        highlight_text: source.highlight_text || match?.highlight_text,
        similarity: typeof source.similarity === 'number' ? source.similarity : match?.similarity,
      };
    });
  }

  private findRetrievedSourceForCitation(citation: AiSource, retrieved: AiSource[]): AiSource | undefined {
    const sameDocument = (candidate: AiSource): boolean => {
      if (citation.doc_uuid && candidate.doc_uuid) {
        return citation.doc_uuid === candidate.doc_uuid;
      }

      const citationTitle = citation.doc_title || citation.title;
      const candidateTitle = candidate.doc_title || candidate.title;

      return !!citationTitle && !!candidateTitle && citationTitle === candidateTitle;
    };

    const articleRef = citation.article_ref || '';
    const page = citation.page || null;

    return retrieved.find(candidate =>
      sameDocument(candidate) &&
      !!articleRef &&
      (candidate.article_ref || '') === articleRef
    ) || retrieved.find(candidate =>
      sameDocument(candidate) &&
      !!page &&
      candidate.page === page
    ) || retrieved.find(candidate => sameDocument(candidate));
  }

  private sourcePassage(source: AiSource): string {
    return [
      source.content,
      source.excerpt,
      source.snippet,
      source.text,
    ].find(value => typeof value === 'string' && value.trim())?.trim() || '';
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  private requestScroll(force = false) {
    if (!force && !this.stickToBottom) {
      return;
    }

    this.shouldScroll = true;
  }

  private openInNewTab(url: string) {
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
